import { createSelector } from 'reselect'
import { AppState } from 'Store'
import moment from 'moment'
import {
	ProductStatus,
	productStatusesAvailableToAddReserve,
	OrderStatus,
	OrderStepType,
} from 'Pages/orders_design'

const DAYS_TO_RESERVE_ACCOUNT = 31

const getAll = (state: AppState) => state.orders_design.orders_design.items
const getTotal = (state: AppState) => state.orders_design.orders_design.total
const getTotalIncomplete = (state: AppState) => state.orders_design.orders_design.totalIncompleted
const getBlockExpiresSoon = (state: AppState) => state.orders_design.orders_design.blockExpiresSoon
const getTotalBackChecking = (state: AppState) =>
	state.orders_design.orders_design.totalBackChecking
const getTotalPartnerRequests = (state: AppState) =>
	state.orders_design.orders_design.totalPartnerRequests
const getParams = (state: AppState) => state.orders_design.orders_design.requestParams
const getListViews = (state: AppState) => state.orders_design.orders_design.ordersListView
const getOrder = (state: AppState) => state.orders_design.orders_design.currentOrder
const getLastOrder = (state: AppState) => state.orders_design.orders_design.items?.entries[0]?.id
const getAcceptBlocked = (state: AppState) => state.orders_design.orders_design.acceptBlocked

const getClientINN = (state: AppState) => state.orders_design.orders_design.clientINN

const getPaperAgreementState = (state: AppState) => state.orders_design.orders_design.paperAgreement

const getOrderFormValues = (state: AppState) => state.orders_design.orders_design.orderFormValues

const getOrderStep = (state: AppState) => state.orders_design.orders_design.step

const getOrderInn = (state: AppState) => state.orders_design.orders_design.currentOrder?.inn

const getDaysForReserveAccountRemaining = createSelector(
	getOrder,
	(order) =>
		DAYS_TO_RESERVE_ACCOUNT - moment(Date.now()).diff(Date.parse(order?.created_at), 'days')
)

const getPossibilityToAddReserveAccount = createSelector(getOrder, (order) =>
	order?.products.some(
		({ reserve_account, status }) =>
			reserve_account && productStatusesAvailableToAddReserve.includes(status?.slug)
	)
)

const getOrderHasAccountedProduct = createSelector(getOrder, (order) =>
	order?.products.some(({ status: { slug } }) => slug === ProductStatus.ACCOUNTED)
)

const isDraftOrder = createSelector(
	getOrder,
	(order) =>
		order?.status_code === OrderStatus.DRAFT ||
		order?.products.every(({ status }) => status?.slug === ProductStatus.DRAFT)
)

const isReserveStep = createSelector(
	getOrderStep,
	(step) => step === OrderStepType.RESERVE_ACCOUNT || step === OrderStepType.WAITING_RESERVE
)

const getOtkritieBankCities = (state: AppState) =>
	state.orders_design.orders_design.otkritieBankCities

const getTotalTasks = (state: AppState) => state.orders_design.orders_design.totalTasks

const getFileOptionsUnblockRequest = (state: AppState) =>
	state.orders_design.orders_design.currentOrder?.files_options?.unblock_request

const getRecognizedData = (state: AppState) => state.orders_design.orders_design.recognizeData
const getRecognizedDataLoading = (state: AppState) =>
	state.orders_design.orders_design.recognizeLoading

const selectConsentShow = (state: AppState) => state.orders_design.orders_design.consentShow

const getSelectedRecommendation = (state: AppState) =>
	state.orders_design.orders_design.selectedRecommendations

const getSelectedProducts = (state: AppState) => state.orders_design.orders_design.selectedProducts

const getSelectedOnlineProducts = (state: AppState) =>
	state.orders_design.orders_design.selectedOnlineProducts

const getBlacklistedInn = (state: AppState) => state.orders_design.orders_design.blacklistedInn
const getAgreementStatus = (state: AppState) => state.orders_design.orders_design.agreementStatus
const getElectronicConsent = (state: AppState) =>
	state.orders_design.orders_design.isElectronicConsent
const getAgreementType = (state: AppState) => state.orders_design.orders_design.agreementType
const getPhoneAgreement = (state: AppState) => state.orders_design.orders_design.phoneAgreement
const getTotalEntriesLoader = (state: AppState) =>
	state.orders_design.orders_design.totalEntriesLoader
const getTotalRejectedReservation = (state: AppState) =>
	state.orders_design.orders_design.totalRejectedReservation

export const orderSelectors = {
	getAll,
	getTotal,
	getTotalIncomplete,
	getTotalBackChecking,
	getTotalPartnerRequests,
	getParams,
	getListViews,
	getOrder,
	getLastOrder,
	getAcceptBlocked,
	getClientINN,
	getPaperAgreementState,
	getOrderFormValues,
	getOrderStep,
	getDaysForReserveAccountRemaining,
	getPossibilityToAddReserveAccount,
	getOrderHasAccountedProduct,
	isDraftOrder,
	isReserveStep,
	getOrderInn,
	getOtkritieBankCities,
	getTotalTasks,
	getFileOptionsUnblockRequest,
	getRecognizedData,
	getRecognizedDataLoading,
	selectConsentShow,
	getSelectedRecommendation,
	getSelectedProducts,
	getSelectedOnlineProducts,
	getBlacklistedInn,
	getBlockExpiresSoon,
	getAgreementStatus,
	getElectronicConsent,
	getAgreementType,
	getPhoneAgreement,
	getTotalEntriesLoader,
	getTotalRejectedReservation,
}
